/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import EventListener from 'react-event-listener';
import { mobileUtils } from '@blend/consumer-components';
import { updateMobileState } from '../../../actions/layoutActions';
import { getMinDesktopWidth, getOrientation } from '../../../common/responsive';
import { useRootSelector } from '../../../hooks/useRootSelector';
import { debounce } from '@utils/lodash';

export namespace MobileWatcher {
  export interface Props {}
}

export const minScreenWidth = getMinDesktopWidth();

export const MobileWatcher: React.FC<MobileWatcher.Props> = () => {
  const dispatch = useDispatch();
  const { isMobileView, orientation } = useRootSelector(state => state.layout);
  const resizeListener = debounce(
    () => {
      const isSmallSizeViewport = window.innerWidth < minScreenWidth;
      const newOrientation = getOrientation(window);
      const needsToUpdate = isMobileView !== isSmallSizeViewport || newOrientation !== orientation;
      if (needsToUpdate) {
        updateMobileState({
          isMobileView: isSmallSizeViewport,
          isMobileDevice: mobileUtils.isMobileDevice(),
          orientation: newOrientation,
        })(dispatch);
      }
    },
    // Using 10ms for the debounce here to minimize the number of times
    // this function runs, which still making sure that the UI updates quickly
    // in the case of a isMobileView change. This seems reasonable because the function
    // is non-blocking and a no-op in the case that the isMobileView doesn't change.
    10,
    { leading: false, trailing: true },
  );

  useEffect(() => {
    resizeListener();
  }, [dispatch, resizeListener]);

  return <EventListener target="window" onResize={resizeListener} />;
};
